import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { RotateCcw, Trophy, Skull, Users, Copy, Eye, Coins, Send, Crown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

// Word categories
const WORD_CATEGORIES = {
  animals: ["elephant", "giraffe", "penguin", "dolphin", "kangaroo", "butterfly", "crocodile", "cheetah", "flamingo", "octopus"],
  movies: ["titanic", "inception", "gladiator", "avatar", "matrix", "godfather", "joker", "frozen", "shrek", "rocky"],
  countries: ["australia", "brazil", "canada", "germany", "japan", "mexico", "france", "italy", "spain", "egypt"],
  sports: ["basketball", "football", "baseball", "tennis", "volleyball", "swimming", "boxing", "cricket", "hockey", "golf"],
  food: ["hamburger", "spaghetti", "chocolate", "sandwich", "pancakes", "avocado", "broccoli", "strawberry", "pineapple", "mushroom"],
  tech: ["computer", "keyboard", "internet", "software", "database", "algorithm", "bluetooth", "processor", "graphics", "firewall"],
  music: ["symphony", "acoustic", "orchestra", "drummer", "vocalist", "harmonica", "saxophone", "keyboard", "amplifier", "microphone"]
};

// Hangman figure parts
const HANGMAN_PARTS = [
  <circle key="head" cx="100" cy="50" r="15" stroke="white" strokeWidth="3" fill="none" />,
  <line key="body" x1="100" y1="65" x2="100" y2="110" stroke="white" strokeWidth="3" />,
  <line key="left-arm" x1="100" y1="80" x2="75" y2="100" stroke="white" strokeWidth="3" />,
  <line key="right-arm" x1="100" y1="80" x2="125" y2="100" stroke="white" strokeWidth="3" />,
  <line key="left-leg" x1="100" y1="110" x2="75" y2="140" stroke="white" strokeWidth="3" />,
  <line key="right-leg" x1="100" y1="110" x2="125" y2="140" stroke="white" strokeWidth="3" />
];

const HangmanGame = ({ currentUser, user, token, API: apiUrl, sendGameMessage, friends, onClose }) => {
  const { token: authToken } = useContext(AuthContext);
  const effectiveToken = token || authToken;
  
  // Game mode state
  const [gameMode, setGameMode] = useState("menu"); // menu, solo, create, join, multiplayer, spectate
  const [gameCode, setGameCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [customWord, setCustomWord] = useState("");
  const [betAmount, setBetAmount] = useState(50);
  const [fchipsBalance, setFchipsBalance] = useState(1000);
  
  // Multiplayer state
  const [isHost, setIsHost] = useState(false);
  const [players, setPlayers] = useState([]);
  const [spectators, setSpectators] = useState([]);
  const [currentGuesser, setCurrentGuesser] = useState(null);
  const [wordCreator, setWordCreator] = useState(null);
  const [gameMessages, setGameMessages] = useState([]);
  const wsRef = useRef(null);
  
  // Game state
  const [word, setWord] = useState("");
  const [category, setCategory] = useState("");
  const [guessedLetters, setGuessedLetters] = useState(new Set());
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [gamePhase, setGamePhase] = useState("waiting"); // waiting, playing, won, lost
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  // Fetch F-Chips balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!effectiveToken) return;
      try {
        const response = await axios.get(`${API}/fchips/balance`, {
          headers: { Authorization: `Bearer ${effectiveToken}` }
        });
        setFchipsBalance(response.data.balance || 0);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    fetchBalance();
  }, [effectiveToken]);

  // Generate game code
  const generateCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  // Solo game start
  const startSoloGame = useCallback(() => {
    const categories = Object.keys(WORD_CATEGORIES);
    const randomCategory = categories[Math.floor(Math.random() * categories.length)];
    const words = WORD_CATEGORIES[randomCategory];
    const randomWord = words[Math.floor(Math.random() * words.length)].toUpperCase();
    
    setWord(randomWord);
    setCategory(randomCategory);
    setGuessedLetters(new Set());
    setWrongGuesses(0);
    setGamePhase("playing");
    setGameMode("solo");
  }, []);

  // Create multiplayer game
  const createMultiplayerGame = () => {
    if (!customWord.trim()) {
      toast.error("Please enter a word for others to guess!");
      return;
    }
    if (customWord.length < 3 || customWord.length > 15) {
      toast.error("Word must be 3-15 characters!");
      return;
    }
    if (!/^[a-zA-Z]+$/.test(customWord)) {
      toast.error("Word must contain only letters!");
      return;
    }
    if (betAmount > fchipsBalance) {
      toast.error("Not enough F-Chips!");
      return;
    }
    
    const code = generateCode();
    setGameCode(code);
    setWord(customWord.toUpperCase());
    setCategory("Custom");
    setIsHost(true);
    setWordCreator(currentUser?.username || user?.username || "Host");
    setPlayers([{ username: currentUser?.username || user?.username || "Host", isHost: true, score: 0 }]);
    setGuessedLetters(new Set());
    setWrongGuesses(0);
    setGamePhase("waiting");
    setGameMode("multiplayer");
    
    // Register game for Quick Join
    axios.post(`${API}/games/register`, {
      game_type: "hangman",
      code: code,
      host: currentUser?.username || user?.username || "Host",
      room_id: null,
      max_players: 8
    }, {
      headers: { Authorization: `Bearer ${effectiveToken}` }
    }).catch(console.error);
    
    toast.success(`Game created! Code: ${code}`);
  };

  // Join multiplayer game
  const joinMultiplayerGame = () => {
    if (!joinCode.trim()) {
      toast.error("Please enter a game code!");
      return;
    }
    // In a real implementation, this would connect via WebSocket
    // For now, we'll simulate joining
    setGameCode(joinCode.toUpperCase());
    setIsHost(false);
    setPlayers(prev => [...prev, { username: currentUser?.username || user?.username || "Player", isHost: false, score: 0 }]);
    setGameMode("multiplayer");
    setGamePhase("playing");
    toast.success(`Joined game ${joinCode.toUpperCase()}!`);
  };

  // Join as spectator
  const joinAsSpectator = () => {
    if (!joinCode.trim()) {
      toast.error("Please enter a game code!");
      return;
    }
    setGameCode(joinCode.toUpperCase());
    setGameMode("spectate");
    setSpectators(prev => [...prev, currentUser?.username || user?.username || "Spectator"]);
    toast.success(`Watching game ${joinCode.toUpperCase()} as spectator!`);
  };

  // Start the game (host only)
  const startGame = () => {
    if (!isHost) return;
    setGamePhase("playing");
    setCurrentGuesser(players.find(p => !p.isHost)?.username || players[0]?.username);
    toast.success("Game started!");
  };

  // Guess letter
  const guessLetter = (letter) => {
    if (gamePhase !== "playing" || guessedLetters.has(letter)) return;
    if (gameMode === "spectate") return; // Spectators can't guess
    if (gameMode === "multiplayer" && isHost && wordCreator === (currentUser?.username || user?.username)) {
      toast.error("You can't guess your own word!");
      return;
    }
    
    const newGuessed = new Set(guessedLetters);
    newGuessed.add(letter);
    setGuessedLetters(newGuessed);
    
    if (!word.includes(letter)) {
      const newWrong = wrongGuesses + 1;
      setWrongGuesses(newWrong);
      addMessage(`${currentUser?.username || "Player"} guessed "${letter}" - WRONG!`, "error");
      
      if (newWrong >= 6) {
        setGamePhase("lost");
        setStreak(0);
        addMessage(`Game Over! The word was "${word}"`, "error");
        if (gameMode === "multiplayer" && !isHost) {
          // Loser pays bet to winner
          addMessage(`${wordCreator} wins ${betAmount} F-Chips!`, "success");
        }
      }
    } else {
      addMessage(`${currentUser?.username || "Player"} guessed "${letter}" - CORRECT!`, "success");
      const won = word.split("").every(l => newGuessed.has(l));
      if (won) {
        setGamePhase("won");
        const bonus = Math.max(0, (6 - wrongGuesses) * 5);
        setScore(prev => prev + 10 + bonus);
        setStreak(prev => prev + 1);
        addMessage(`🎉 ${currentUser?.username || "Player"} wins! Word: "${word}"`, "success");
        if (gameMode === "multiplayer" && !isHost) {
          // Winner gets bet
          addMessage(`You win ${betAmount * 2} F-Chips!`, "success");
        }
      }
    }
  };

  const addMessage = (text, type = "info") => {
    setGameMessages(prev => [...prev.slice(-9), { text, type, time: new Date().toLocaleTimeString() }]);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(gameCode);
    toast.success("Game code copied!");
  };

  const keyboard = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // ==================== MENU SCREEN ====================
  if (gameMode === "menu") {
    return (
      <div className="w-full h-full flex flex-col bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-2 flex items-center justify-between border-b border-gray-700">
          <span className="text-yellow-400 font-bold text-lg">🎯 HANGMAN</span>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md space-y-4">
            {/* F-Chips Balance */}
            <div className="bg-green-900/50 rounded-lg p-3 flex items-center justify-center gap-2">
              <Coins className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-bold">F-CHIPS: {fchipsBalance.toLocaleString()}</span>
            </div>
            
            {/* Solo Play */}
            <button
              onClick={startSoloGame}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg text-white font-bold text-lg shadow-lg"
            >
              🎮 SOLO PLAY
            </button>
            
            {/* Create Game */}
            <button
              onClick={() => setGameMode("create")}
              className="w-full py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-lg text-white font-bold text-lg shadow-lg"
            >
              <div className="flex items-center justify-center gap-2">
                <Crown className="w-5 h-5" />
                CREATE MULTIPLAYER GAME
              </div>
            </button>
            
            {/* Join Game */}
            <button
              onClick={() => setGameMode("join")}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 rounded-lg text-white font-bold text-lg shadow-lg"
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="w-5 h-5" />
                JOIN GAME
              </div>
            </button>
            
            <div className="text-center text-gray-400 text-sm mt-4">
              Create a word for others to guess, or join and guess someone else's word!
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== CREATE GAME SCREEN ====================
  if (gameMode === "create") {
    return (
      <div className="w-full h-full flex flex-col bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-2 flex items-center justify-between border-b border-gray-700">
          <span className="text-yellow-400 font-bold">🎯 CREATE HANGMAN GAME</span>
          <button onClick={() => setGameMode("menu")} className="text-gray-400 hover:text-white">← Back</button>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md space-y-4 bg-gray-800/50 rounded-xl p-6">
            <div>
              <label className="text-gray-300 text-sm font-bold block mb-2">Your Secret Word</label>
              <input
                type="text"
                value={customWord}
                onChange={(e) => setCustomWord(e.target.value.replace(/[^a-zA-Z]/g, ''))}
                placeholder="Enter a word (3-15 letters)"
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-yellow-400 outline-none"
                maxLength={15}
              />
              <p className="text-gray-500 text-xs mt-1">Others will try to guess this word</p>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm font-bold block mb-2">
                <Coins className="w-4 h-4 inline mr-1 text-yellow-400" />
                Bet Amount (F-Chips)
              </label>
              <div className="flex gap-2">
                {[50, 100, 250, 500].map(amount => (
                  <button
                    key={amount}
                    onClick={() => setBetAmount(amount)}
                    className={`flex-1 py-2 rounded-lg font-bold ${
                      betAmount === amount 
                        ? 'bg-yellow-500 text-black' 
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {amount}
                  </button>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-1">Winner takes all! Your balance: {fchipsBalance}</p>
            </div>
            
            <button
              onClick={createMultiplayerGame}
              disabled={!customWord.trim() || betAmount > fchipsBalance}
              className="w-full py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold text-lg"
            >
              CREATE GAME
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== JOIN GAME SCREEN ====================
  if (gameMode === "join") {
    return (
      <div className="w-full h-full flex flex-col bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-2 flex items-center justify-between border-b border-gray-700">
          <span className="text-yellow-400 font-bold">🎯 JOIN HANGMAN GAME</span>
          <button onClick={() => setGameMode("menu")} className="text-gray-400 hover:text-white">← Back</button>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md space-y-4 bg-gray-800/50 rounded-xl p-6">
            <div>
              <label className="text-gray-300 text-sm font-bold block mb-2">Game Code</label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Enter 6-character code"
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-yellow-400 outline-none text-center text-xl font-mono tracking-widest"
                maxLength={6}
              />
            </div>
            
            <button
              onClick={joinMultiplayerGame}
              disabled={joinCode.length !== 6}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold text-lg"
            >
              <Users className="w-5 h-5 inline mr-2" />
              JOIN AS PLAYER
            </button>
            
            <button
              onClick={joinAsSpectator}
              disabled={joinCode.length !== 6}
              className="w-full py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold"
            >
              <Eye className="w-5 h-5 inline mr-2" />
              WATCH AS SPECTATOR
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== MULTIPLAYER / SPECTATE GAME ====================
  return (
    <div className="w-full h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-2 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-yellow-400 font-bold">🎯 HANGMAN</span>
          {gameMode === "multiplayer" && (
            <div className="flex items-center gap-2">
              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded font-mono">{gameCode}</span>
              <button onClick={copyCode} className="text-gray-400 hover:text-white">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
          {gameMode === "spectate" && (
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
              <Eye className="w-3 h-3" /> SPECTATING
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {gameMode !== "solo" && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Users className="w-4 h-4" />
              {players.length} players
              {spectators.length > 0 && <span className="text-blue-400">• {spectators.length} watching</span>}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <span className="text-white font-bold text-sm">{score}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-orange-400">🔥</span>
            <span className="text-white font-bold text-sm">{streak}</span>
          </div>
          {gameMode === "multiplayer" && (
            <div className="flex items-center gap-1">
              <Coins className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 font-bold text-sm">{betAmount}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Game Area - Horizontal Layout */}
      <div className="flex-1 flex">
        {/* LEFT SIDE - Hangman Drawing & Players */}
        <div className="w-1/4 flex flex-col bg-gray-800 border-r border-gray-700">
          {/* Hangman Drawing */}
          <div className="flex-1 flex flex-col items-center justify-center p-2">
            <div className="text-gray-400 text-xs mb-2">HANGMAN</div>
            <div className="bg-gray-900 rounded-lg p-2">
              <svg width="160" height="140" className="drop-shadow-lg">
                {/* Gallows */}
                <line x1="25" y1="130" x2="135" y2="130" stroke="#8B4513" strokeWidth="4" />
                <line x1="50" y1="130" x2="50" y2="10" stroke="#8B4513" strokeWidth="4" />
                <line x1="50" y1="10" x2="100" y2="10" stroke="#8B4513" strokeWidth="4" />
                <line x1="100" y1="10" x2="100" y2="30" stroke="#CD853F" strokeWidth="2" />
                {/* Hangman parts */}
                {HANGMAN_PARTS.slice(0, wrongGuesses)}
              </svg>
            </div>
            <div className="text-red-400 text-xs mt-1">Wrong: {wrongGuesses}/6</div>
          </div>
          
          {/* Players List */}
          {gameMode !== "solo" && (
            <div className="p-2 border-t border-gray-700">
              <div className="text-gray-400 text-xs mb-1">PLAYERS</div>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {players.map((p, i) => (
                  <div key={i} className={`text-xs px-2 py-1 rounded flex items-center justify-between ${
                    p.isHost ? 'bg-yellow-900/50 text-yellow-400' : 'bg-gray-700 text-white'
                  }`}>
                    <span className="flex items-center gap-1">
                      {p.isHost && <Crown className="w-3 h-3" />}
                      {p.username}
                    </span>
                    <span className="text-gray-400">{p.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CENTER - The Oval Table */}
        <div className="flex-1 relative bg-gray-800 overflow-hidden flex items-center justify-center">
          {/* Waiting for players */}
          {gamePhase === "waiting" && isHost && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20">
              <div className="bg-gray-800 rounded-xl p-6 text-center">
                <div className="text-yellow-400 font-bold text-xl mb-2">Waiting for Players</div>
                <div className="text-gray-400 text-sm mb-4">Share this code:</div>
                <div className="bg-gray-900 px-6 py-3 rounded-lg font-mono text-2xl text-white mb-4">{gameCode}</div>
                <button onClick={copyCode} className="text-blue-400 hover:text-blue-300 text-sm mb-4 flex items-center gap-1 mx-auto">
                  <Copy className="w-4 h-4" /> Copy Code
                </button>
                {players.length >= 2 && (
                  <button
                    onClick={startGame}
                    className="w-full py-3 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold"
                  >
                    START GAME ({players.length} players)
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Oval Table */}
          <div className="w-[85%] h-[80%] rounded-[50%] bg-gradient-to-b from-green-700 to-green-900 border-8 border-amber-700 shadow-[0_0_40px_rgba(0,0,0,0.5),inset_0_0_40px_rgba(0,0,0,0.3)] flex items-center justify-center">
            <div className="absolute inset-8 border-2 border-amber-600/30 rounded-[50%]" />
            
            {/* Word Display - Center of Table */}
            <div className="text-center z-10">
              <div className="text-gray-400 text-xs mb-2 uppercase">
                {gameMode === "multiplayer" ? `Word by ${wordCreator}` : category}
              </div>
              <div className="flex justify-center flex-wrap gap-2 max-w-[300px]">
                {word.split("").map((letter, i) => (
                  <div key={i} className="w-10 h-12 border-b-4 border-white flex items-end justify-center pb-1">
                    <span className="text-white text-2xl font-bold">
                      {guessedLetters.has(letter) || gamePhase === "lost" ? letter : ""}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-gray-300 text-xs mt-3">{word.length} letters</div>
            </div>

            {/* Result Banner */}
            {(gamePhase === "won" || gamePhase === "lost") && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-[50%]">
                <div className={`px-8 py-4 rounded-xl ${gamePhase === "won" ? "bg-green-600" : "bg-red-600"}`}>
                  <div className="text-center text-white">
                    <div className="text-3xl font-bold mb-2 flex items-center gap-2 justify-center">
                      {gamePhase === "won" ? (
                        <>🎉 <Trophy className="w-8 h-8" /> YOU WIN!</>
                      ) : (
                        <>💀 <Skull className="w-8 h-8" /> GAME OVER</>
                      )}
                    </div>
                    <div className="text-lg">
                      {gamePhase === "won" 
                        ? `+${10 + Math.max(0, (6 - wrongGuesses) * 5)} points!` 
                        : `Word: ${word}`}
                    </div>
                    {gameMode === "multiplayer" && (
                      <div className="text-yellow-300 mt-2">
                        {gamePhase === "won" ? `🪙 +${betAmount * 2} F-Chips!` : `🪙 -${betAmount} F-Chips`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - Guessed Letters & Messages */}
        <div className="w-1/4 flex flex-col bg-gray-800 border-l border-gray-700">
          {/* Correct Letters */}
          <div className="p-2">
            <div className="text-gray-400 text-xs mb-1">CORRECT</div>
            <div className="flex gap-1 flex-wrap">
              {Array.from(guessedLetters).filter(l => word.includes(l)).map((l, i) => (
                <span key={i} className="w-6 h-6 bg-green-600 rounded flex items-center justify-center text-white font-bold text-sm">
                  {l}
                </span>
              ))}
              {Array.from(guessedLetters).filter(l => word.includes(l)).length === 0 && (
                <span className="text-gray-500 text-xs">None yet</span>
              )}
            </div>
          </div>
          
          {/* Wrong Letters */}
          <div className="p-2 border-t border-gray-700">
            <div className="text-gray-400 text-xs mb-1">WRONG</div>
            <div className="flex gap-1 flex-wrap">
              {Array.from(guessedLetters).filter(l => !word.includes(l)).map((l, i) => (
                <span key={i} className="w-6 h-6 bg-red-600/50 rounded flex items-center justify-center text-white font-bold text-sm">
                  {l}
                </span>
              ))}
            </div>
          </div>
          
          {/* Game Messages */}
          {gameMode !== "solo" && (
            <div className="flex-1 p-2 border-t border-gray-700 flex flex-col">
              <div className="text-gray-400 text-xs mb-1">GAME LOG</div>
              <div className="flex-1 bg-gray-900 rounded p-2 overflow-y-auto text-xs space-y-1">
                {gameMessages.map((msg, i) => (
                  <div key={i} className={`${
                    msg.type === 'success' ? 'text-green-400' : 
                    msg.type === 'error' ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    <span className="text-gray-600">[{msg.time}]</span> {msg.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM - Keyboard */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 border-t border-gray-700">
        {(gamePhase === "playing" && gameMode !== "spectate") ? (
          <div className="space-y-1">
            <div className="flex justify-center gap-1">
              {keyboard.slice(0, 10).map(letter => (
                <button key={letter} onClick={() => guessLetter(letter)} disabled={guessedLetters.has(letter)}
                  className={`w-8 h-9 rounded font-bold text-sm ${guessedLetters.has(letter) ? (word.includes(letter) ? 'bg-green-600 text-white' : 'bg-red-600/50 text-gray-400') : 'bg-gray-600 hover:bg-gray-500 text-white'} disabled:cursor-not-allowed`}
                >{letter}</button>
              ))}
            </div>
            <div className="flex justify-center gap-1">
              {keyboard.slice(10, 19).map(letter => (
                <button key={letter} onClick={() => guessLetter(letter)} disabled={guessedLetters.has(letter)}
                  className={`w-8 h-9 rounded font-bold text-sm ${guessedLetters.has(letter) ? (word.includes(letter) ? 'bg-green-600 text-white' : 'bg-red-600/50 text-gray-400') : 'bg-gray-600 hover:bg-gray-500 text-white'} disabled:cursor-not-allowed`}
                >{letter}</button>
              ))}
            </div>
            <div className="flex justify-center gap-1">
              {keyboard.slice(19).map(letter => (
                <button key={letter} onClick={() => guessLetter(letter)} disabled={guessedLetters.has(letter)}
                  className={`w-8 h-9 rounded font-bold text-sm ${guessedLetters.has(letter) ? (word.includes(letter) ? 'bg-green-600 text-white' : 'bg-red-600/50 text-gray-400') : 'bg-gray-600 hover:bg-gray-500 text-white'} disabled:cursor-not-allowed`}
                >{letter}</button>
              ))}
            </div>
          </div>
        ) : gameMode === "spectate" ? (
          <div className="text-center text-gray-400 py-2">
            <Eye className="w-5 h-5 inline mr-2" />
            You are spectating - sit back and watch!
          </div>
        ) : (
          <button 
            onClick={gameMode === "solo" ? startSoloGame : () => setGameMode("menu")} 
            className="w-full py-3 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" /> {gameMode === "solo" ? "NEW GAME" : "BACK TO MENU"}
          </button>
        )}
      </div>
    </div>
  );
};

export default HangmanGame;
